/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { Else, If, Then, When } from "react-if";
import { useParams } from "react-router-dom";
import { Spacer, Tab, Tabs, theme } from "@nordcloud/gnui";
import { CostBox } from "~/components/CostBox";
import { useCurrency, useDisclosure, useFeature } from "~/hooks";
import { isNotEmpty, isNotNil, noop } from "~/tools";
import { AccountCredentialsUpdateSidebar } from "../AccountCredentialsUpdateSidebar/AccountCredentialsUpdateSidebar";
import {
  HybridOnboardSidebar,
  ClusterUtilization,
  ChargeTypeCostsBar,
} from "../components";
import {
  getAccountDisplayName,
  isKubernetesOrOpenshift,
  mapProviderTab,
} from "../utils";
import {
  Details,
  DetailsForm,
  DetailsHeader,
  DetailsNotification,
  HybridCloudDocumentationSidebar,
} from "./Details";
import { EstateRecords } from "./Estate/EstateRecords";
import { Header } from "./Header";
import { useCloudAccountDetails } from "./hooks/useCloudAccountDetails";
import { EstateRecords as OldEstateList } from "./Tabs";

export function CloudAccountDetailsPage() {
  const showEstateRecords = useFeature("estateRecordsV2");

  const {
    open: openCredentialsUpdateSteps,
    close: closeCredentialsUpdateSteps,
    isOpen: showCredentialsUpdateSteps,
  } = useDisclosure();

  const {
    open: openDocumentationSidebar,
    close: closeDocumentationSidebar,
    isOpen: isOpenDocumentationSidebar,
  } = useDisclosure();

  const {
    open: openOnboardSidebar,
    close: closeOnboardSidebar,
    isOpen: isOpenOnboardSidebar,
  } = useDisclosure();

  const { nid = "" } = useParams<RouteParams>();

  const {
    account,
    tags,
    showUpdateCredentials,
    chargeTypeCosts,
    accountSavingsCosts,
    accountSpends,
  } = useCloudAccountDetails(nid);

  const { currency } = useCurrency();

  const showClusterUtilization = isNotNil(account?.providerType)
    ? isKubernetesOrOpenshift(account?.providerType)
    : false;

  const providerTab = mapProviderTab(account?.provider, account?.providerType);

  return (
    <>
      <When condition={isNotNil(account)}>
        <Header
          tags={tags}
          account={account}
          openCredentialsUpdateSteps={openCredentialsUpdateSteps}
          showUpdateCredentials={showUpdateCredentials}
          openDocumentationSidebar={openDocumentationSidebar}
          openOnboardSidebar={openOnboardSidebar}
        />
        <DetailsNotification
          account={account}
          openCredentialsUpdateSteps={openCredentialsUpdateSteps}
          openDocumentationSidebar={openDocumentationSidebar}
          openOnboardSidebar={openOnboardSidebar}
        />
        <Details>
          <div css={{ width: "70%" }}>
            <DetailsHeader
              accountName={getAccountDisplayName(account)}
              provider={account?.provider}
              providerType={account?.providerType}
              cloudStatus={account?.cloudStatus}
            />
            <DetailsForm
              accountInternalId={nid}
              accountId={account?.providerId}
              accountOwner={account?.owner}
              description={account?.description ?? ""}
              contactsNames={(account?.contacts ?? [])
                .map((contactPerson) => contactPerson?.name ?? "")
                .join(", ")}
            />
          </div>
          <div css={{ width: "30%", height: "18.5rem" }}>
            <CostBox
              numericalValues={{
                forecast: accountSpends.currentMonthForecastValue,
                currentMonth: accountSpends.currentMonthValue,
                previousMonth: accountSpends.previousMonthValue,
                currency,
                budget: 0,
                cloudWasteCost: accountSavingsCosts.cost,
                co2e: accountSpends.currentMonthCo2e,
                co2eSavings: accountSavingsCosts.co2e,
                originalCost: 0,
                reservedInstanceBenefit: 0,
              }}
            />
          </div>
        </Details>
        <When condition={isNotEmpty(chargeTypeCosts)}>
          <Spacer height={theme.spacing.spacing04} />
          <ChargeTypeCostsBar chargeTypeCosts={chargeTypeCosts} />
        </When>
        <When condition={showClusterUtilization}>
          <Spacer height={theme.spacing.spacing04} />
          <ClusterUtilization nordcloudId={account?.nid} />
        </When>
        <If condition={showEstateRecords}>
          <Then>
            <EstateRecords
              providerId={account?.providerId}
              providerType={account?.providerType}
              provider={account?.provider}
            />
          </Then>
          <Else>
            <Spacer height={theme.spacing.spacing04} />
            <Tabs handleTab={noop} step={0}>
              <Tab heading="Estate Records" caption="Account estate records">
                <OldEstateList
                  providerId={account?.providerId}
                  providerType={account?.providerType ?? undefined}
                  provider={account?.provider}
                />
              </Tab>
            </Tabs>
          </Else>
        </If>
        <When condition={showUpdateCredentials && isNotNil(providerTab)}>
          {providerTab && (
            <AccountCredentialsUpdateSidebar
              nids={[nid]}
              provider={providerTab}
              isOpen={showCredentialsUpdateSteps}
              onClose={closeCredentialsUpdateSteps}
            />
          )}
        </When>
        <HybridCloudDocumentationSidebar
          account={account}
          isOpen={isOpenDocumentationSidebar}
          onClose={closeDocumentationSidebar}
        />
        <HybridOnboardSidebar
          account={account}
          isOpen={isOpenOnboardSidebar}
          onClose={closeOnboardSidebar}
        />
      </When>
    </>
  );
}

type RouteParams = {
  nid: string;
};
